import isEmpty from "lodash/isEmpty";
import i18n from "i18n";
import Pivot, { DateType, ValuesType } from "views/logic/aggregationbuilder/Pivot";
import generateId from "logic/generateId";
import parseNumber from "views/components/aggregationwizard/grouping/parseNumber";
import { DEFAULT_PIVOT_LIMIT, DEFAULT_PIVOT_INTERVAL } from "views/Constants";
import GroupingsConfiguration from "./GroupingsConfiguration";
export const toValuesGrouping = (grouping) => {
  const newGrouping = {
    ...grouping,
    type: ValuesType,
    limit: DEFAULT_PIVOT_LIMIT
  };
  if ("interval" in newGrouping) {
    delete newGrouping.interval;
  }
  return newGrouping;
};
export const toTimeGrouping = (grouping) => {
  const newGrouping = {
    ...grouping,
    type: DateType,
    interval: DEFAULT_PIVOT_INTERVAL
  };
  if ("limit" in newGrouping) {
    delete newGrouping.limit;
  }
  return newGrouping;
};
export const onGroupingFieldsChange = ({
  fieldTypes,
  groupingIndex,
  grouping,
  newFields,
  setFieldValue
}) => {
  const updateFormState = (data) => setFieldValue(`groupBy.groupings.${groupingIndex}`, data);
  if (!newFields.length) {
    updateFormState({
      ...toValuesGrouping(grouping),
      fields: []
    });
    return;
  }
  const isKnownDateField = (fieldName) => fieldTypes.currentQuery.some(({ name, type }) => name === fieldName && type.type === "date");
  const groupingHasValuesField = newFields.some((fieldName) => !isKnownDateField(fieldName));
  const newGroupingType = groupingHasValuesField ? ValuesType : DateType;
  if (grouping.type === newGroupingType) {
    updateFormState({ ...grouping, fields: newFields });
    return;
  }
  if (grouping.type !== newGroupingType) {
    if (newGroupingType === ValuesType) {
      updateFormState({
        ...toValuesGrouping(grouping),
        fields: newFields
      });
    }
    if (newGroupingType === DateType) {
      updateFormState({
        ...toTimeGrouping(grouping),
        fields: newFields
      });
    }
  }
};
const validateDateGrouping = (grouping) => {
  var _a;
  const groupByError = {};
  if (!((_a = grouping.fields) == null ? void 0 : _a.length)) {
    groupByError.fields = i18n.t("wizard_common.field_required", { ns: "aggregation" });
  }
  if (grouping.interval.type === "auto") {
    if (!grouping.interval.scaling) {
      groupByError.interval = i18n.t("wizard_grouping.scaling_required", { ns: "aggregation" });
    }
    if (grouping.interval.scaling && (grouping.interval.scaling <= 0 || grouping.interval.scaling >= 10)) {
      groupByError.interval = i18n.t("wizard_grouping.scaling_range", { ns: "aggregation" });
    }
  }
  if (grouping.interval.type === "timeunit") {
    if (!grouping.interval.value) {
      groupByError.interval = i18n.t("wizard_grouping.value_required", { ns: "aggregation" });
    }
    if (grouping.interval.value && grouping.interval.value <= 0) {
      groupByError.interval = i18n.t("wizard_common.must_be_greater_than_zero", { ns: "aggregation" });
    }
  }
  return groupByError;
};
const validateValuesGrouping = (grouping) => {
  var _a;
  const groupByError = {};
  if (!((_a = grouping.fields) == null ? void 0 : _a.length)) {
    groupByError.fields = i18n.t("wizard_common.field_required", { ns: "aggregation" });
  }
  const parsedLimit = parseNumber(grouping.limit);
  if (parsedLimit === void 0) {
    groupByError.limit = i18n.t("wizard_grouping.limit_required", { ns: "aggregation" });
  }
  if (grouping.limit <= 0) {
    groupByError.limit = i18n.t("wizard_common.must_be_greater_than_zero", { ns: "aggregation" });
  }
  return groupByError;
};
const hasErrors = (errors) => errors.filter((error) => Object.keys(error).length > 0).length > 0;
const validateGrouping = (grouping) => {
  if ("interval" in grouping) {
    return validateDateGrouping(grouping);
  }
  return validateValuesGrouping(grouping);
};
const validateGroupBy = ({ values }) => {
  var _a, _b, _c;
  if (((_a = values == null ? void 0 : values.visualization) == null ? void 0 : _a.type) === "numeric" && ((_c = (_b = values == null ? void 0 : values.groupBy) == null ? void 0 : _b.groupings) == null ? void 0 : _c.length) > 0) {
    return { groupBy: "Single Number widget does not support Group By" };
  }
  return {};
};
const validateGroupings = (values) => {
  const emptyErrors = {};
  if (!values.groupBy) {
    return emptyErrors;
  }
  const { groupings } = values.groupBy;
  const groupingErrors = groupings.map(validateGrouping);
  const groupByErrors = validateGroupBy({ values });
  if (hasErrors([groupByErrors])) return groupByErrors;
  if (hasErrors(groupingErrors)) return { groupBy: { groupings: groupingErrors } };
  return emptyErrors;
};
const addRandomId = (baseGrouping) => ({
  ...baseGrouping,
  id: generateId()
});
const datePivotToGrouping = (pivot, direction) => {
  const { fields, config } = pivot;
  const { interval } = config;
  return addRandomId({
    direction,
    fields,
    type: DateType,
    interval
  });
};
const valuesPivotToGrouping = (pivot, direction) => {
  const { fields, config } = pivot;
  const { limit, skip_empty_values } = config;
  return addRandomId({
    direction,
    fields,
    type: ValuesType,
    limit,
    skipEmptyValues: skip_empty_values
  });
};
const pivotToGroupings = (pivot, direction) => {
  if (pivot.type === DateType) {
    return datePivotToGrouping(pivot, direction);
  }
  if (pivot.type === ValuesType) {
    return valuesPivotToGrouping(pivot, direction);
  }
  throw new Error(`Widget has not supported pivot type "${pivot.type}"`);
};
const pivotsToGrouping = (config) => {
  const transformedRowPivots = config.rowPivots.map((pivot) => pivotToGroupings(pivot, "row"));
  const transformedColumnPivots = config.columnPivots.map((pivot) => pivotToGroupings(pivot, "column"));
  return [...transformedRowPivots, ...transformedColumnPivots];
};
const groupingToPivot = (grouping) => {
  const pivotConfig = "interval" in grouping ? { interval: grouping.interval } : { limit: parseNumber(grouping.limit), skip_empty_values: grouping.skipEmptyValues };
  return Pivot.create(grouping.fields, grouping.type, pivotConfig);
};
const groupByToConfig = (groupBy, config) => {
  const rowPivots = groupBy.groupings.filter((grouping) => grouping.direction === "row").map(groupingToPivot);
  const columnPivots = groupBy.groupings.filter((grouping) => grouping.direction === "column").map(groupingToPivot);
  const { columnRollup } = groupBy;
  return config.rowPivots(rowPivots).columnPivots(columnPivots).rollup(columnRollup);
};
export const createEmptyGrouping = () => addRandomId({
  direction: "row",
  fields: [],
  type: ValuesType,
  limit: DEFAULT_PIVOT_LIMIT
});
const GroupByElement = {
  sectionTitle: "Group By",
  sectionTitleKey: "wizard_common.group_by",
  title: "Grouping",
  titleKey: "wizard_common.grouping",
  key: "groupBy",
  order: 1,
  allowCreate: (formValues) => !(formValues.visualization.type === "numeric"),
  onCreate: (formValues) => {
    var _a, _b;
    return {
      ...formValues,
      groupBy: {
        columnRollup: formValues.groupBy ? formValues.groupBy.columnRollup : false,
        groupings: [...(_b = (_a = formValues.groupBy) == null ? void 0 : _a.groupings) != null ? _b : [], createEmptyGrouping()]
      }
    };
  },
  onRemove: (index, formValues) => {
    var _a, _b;
    const newFormValues = { ...formValues };
    const newGroupings = (_a = formValues.groupBy) == null ? void 0 : _a.groupings.filter((_value, i) => index !== i);
    return {
      ...newFormValues,
      groupBy: {
        columnRollup: (_b = newFormValues.groupBy.columnRollup) != null ? _b : false,
        groupings: newGroupings
      }
    };
  },
  fromConfig: (config) => {
    const groupings = pivotsToGrouping(config);
    if (isEmpty(groupings)) {
      return void 0;
    }
    return {
      groupBy: {
        columnRollup: config.rollup,
        groupings
      }
    };
  },
  toConfig: (formValues, configBuilder) => groupByToConfig(formValues.groupBy, configBuilder),
  component: GroupingsConfiguration,
  validate: validateGroupings,
  isEmpty: (formValues) => {
    var _a;
    return ((_a = formValues == null ? void 0 : formValues.groupings) != null ? _a : []).length === 0;
  }
};
export default GroupByElement;
